/**
 * SupportService.js — Service frontend pour le système de support/ticketing (Phase 1).
 *
 * SPEC : SPEC-2026-06-10-support-desk.v2.md §4
 * §5/§9 — Logique d'appel API centralisée ici ; toutes les pages importent depuis ce service.
 * Via axiosWithToken (chiffrement GCM bout-en-bout).
 */
import { axiosWithToken } from '../utils/functions/apiCallWithToken';
import { SERVER_URL as serverURL } from '../core/config';


/* ══════════════════════════════════════════════════════════════
   PORTAIL UTILISATEUR
══════════════════════════════════════════════════════════════ */

/**
 * Crée un nouveau ticket de support.
 * @param {{ subject: string, category: string, priority?: string, body: string, attachments?: [] }} data
 */
export const createTicket = async (data) => {
  return axiosWithToken(`${serverURL}/support/tickets`, 'post', data);
};

/**
 * Liste les tickets de l'utilisateur connecté.
 */
export const listMyTickets = async () => {
  return axiosWithToken(`${serverURL}/support/tickets`, 'get');
};

/**
 * Récupère un ticket + ses messages (hors notes internes).
 * @param {string|number} ticketId
 */
export const getMyTicket = async (ticketId) => {
  return axiosWithToken(`${serverURL}/support/tickets/${ticketId}`, 'get');
};

/**
 * Envoie une réponse sur un ticket de l'utilisateur.
 * @param {string|number} ticketId
 * @param {{ body: string, attachments?: [] }} data
 */
export const replyToMyTicket = async (ticketId, data) => {
  return axiosWithToken(`${serverURL}/support/tickets/${ticketId}/messages`, 'post', data);
};

export const reopenTicket = async (ticketId) => {
  return axiosWithToken(`${serverURL}/support/tickets/${ticketId}/reopen`, 'put');
};

/* ══════════════════════════════════════════════════════════════
   CONSOLE AGENT (isSupport / isAdmin)
══════════════════════════════════════════════════════════════ */

/**
 * Liste les tickets avec filtres.
 * @param {{ status?: string, priority?: string, category?: string, assigned?: string, q?: string, page?: number }} params
 */
export const listAgentTickets = async (params = {}) => {
  return axiosWithToken(`${serverURL}/support/agent/tickets`, 'get', {}, params);
};

/**
 * Récupère un ticket + messages (inclut les notes internes).
 * @param {string|number} ticketId
 */
export const getAgentTicket = async (ticketId) => {
  return axiosWithToken(`${serverURL}/support/agent/tickets/${ticketId}`, 'get');
};

/**
 * Envoie une réponse agent (publique ou note interne).
 * @param {string|number} ticketId
 * @param {{ body: string, isInternalNote?: boolean }} data
 */
export const agentReply = async (ticketId, data) => {
  return axiosWithToken(`${serverURL}/support/agent/tickets/${ticketId}/messages`, 'post', data);
};

/**
 * Modifie le statut, la priorité, l'assignation ou la catégorie d'un ticket.
 * @param {string|number} ticketId
 * @param {{ status?: string, priority?: string, assignedAgentId?: number, category?: string }} data
 */
export const patchAgentTicket = async (ticketId, data) => {
  return axiosWithToken(`${serverURL}/support/agent/tickets/${ticketId}`, 'patch', data);
};

/**
 * Récupère les compteurs de statut (stats pour la console agent).
 */
export const getAgentStats = async () => {
  return axiosWithToken(`${serverURL}/support/agent/stats`, 'get');
};

/* ══════════════════════════════════════════════════════════════
   HELPLINE (Chatwoot SSO) — isSupport / isAdmin
══════════════════════════════════════════════════════════════ */

/**
 * Récupère une URL de connexion SSO à la helpline Chatwoot pour l'agent courant.
 * Backend : GET /support/chat/sso [verifyToken, isSupport] → { url }.
 * @returns {Promise<{ url: string }>}
 */
export const getChatSso = async () => {
  return axiosWithToken(`${serverURL}/support/chat/sso`, 'get');
};

/**
 * Ouvre la console helpline (Chatwoot) dans un nouvel onglet via SSO.
 * Universel : marche sur tous les domaines (earniverse, earnimarket…) sans le souci
 * de cookie cross-domaine d'un iframe intégré. L'onglet est ouvert de façon synchrone
 * dans le geste de clic (anti-popup-blocker), puis on y navigue vers l'URL SSO.
 */
export const openHelplineInNewTab = async () => {
  const win = window.open('about:blank', '_blank');
  try {
    const res = await getChatSso();
    const url = res && res.url;
    if (!url) throw new Error('no helpline url');
    if (win) win.location.href = url;
    else window.location.href = url;
  } catch (e) {
    if (win) win.close();
    throw e;
  }
};

/* ══════════════════════════════════════════════════════════════
   GESTION DES UTILISATEURS (isAdmin seulement)
══════════════════════════════════════════════════════════════ */

/**
 * Recherche des utilisateurs inscrits.
 * @param {{ q?: string, page?: number }} params
 */
export const listAdminUsers = async (params = {}) => {
  return axiosWithToken(`${serverURL}/support/admin/users`, 'get', {}, params);
};

/**
 * Modifie les droits d'un utilisateur (isSupport et/ou isAdmin → roleId).
 * @param {number} userId
 * @param {{ isSupport?: boolean, isAdmin?: boolean }} data
 */
export const patchAdminUser = async (userId, data) => {
  return axiosWithToken(`${serverURL}/support/admin/users/${userId}`, 'patch', data);
};
